import {useCallback, useMemo} from 'react';

export interface TrackingGroup {
  name: string;
  identifier: string;
  properties?: Record<string, any>;
}

interface Args {
  action?: string;
  version: string;
  category?: string;
  payload?: Record<string, any>;
  trackingGroup?: TrackingGroup;
  disabled?: boolean;
}

export const useTrackingEvents = ({trackingGroup, category, payload, action, disabled, version}: Args) => {
  const trackGroup = useCallback((group: TrackingGroup) => {
    const groupName = group.name;
    const identifier = group.identifier;
    const properties = group.properties;

    if (properties) {
      window.posthog?.group(groupName, identifier, properties);
    } else {
      window.posthog?.group(groupName, identifier);
    }
  }, []);

  const trackEvent = (eventName: string, properties?: Record<string, any>) => {
    if (properties) {
      window.posthog?.capture(eventName, properties);
    } else {
      window.posthog?.capture(eventName);
    }
  };

  const parseArguments = (overrides: Partial<Args>): Args => ({
    trackingGroup: overrides.trackingGroup || trackingGroup,
    version: overrides.version || version,
    category: overrides.category || category,
    action: overrides.action || action,
    payload: overrides.payload || payload,
  });

  const dispatchTracking = (overrides: Partial<Args>) => {
    const parsed = parseArguments(overrides);

    if (!parsed.action && !parsed.trackingGroup) {
      console.warn('Nothing to track...');
      return;
    }

    if (parsed.trackingGroup) {
      trackGroup(parsed.trackingGroup);
    }

    if (parsed.action) {
      const eventName = parsed.category
        ? `${parsed.version}:${parsed.category}:${parsed.action}`
        : `${parsed.version}:${parsed.category}`;
      trackEvent(eventName, parsed.payload);
    }
  };

  const identifyUser = (identifier: string) => {
    window.posthog?.identify(identifier);
  };

  const eventAttributes = useMemo(() => {
    if (disabled) {
      return {};
    }
    const eventName = category ? `${version}:${category}:${action}` : `${version}:${action}`;

    return {
      'data-tracking-event': action ? eventName : undefined,
      'data-tracking-payload': payload ? JSON.stringify(payload) : undefined,
      'data-tracking-group': trackingGroup ? JSON.stringify(trackingGroup) : undefined,
    };
  }, [action, payload, trackingGroup, disabled, category, version]);

  return {
    // attaches tracking attributes to html and allows `tracking-events.js` to handle dispatching events
    eventAttributes,
    // When tracking occurs outside DOM events, this dispatches manual tracking.
    dispatchTracking,
    trackGroup,
    identifyUser,
  };
};
